import AssetIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/AssetIcon';
import DollarIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/DollarIcon';
import DrawCreditCardIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/DrawCreditCardIcon';
import HomeIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/HomeIcon';
import UserIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/UserIcon';
import _ from 'lodash';

import {
  TEAM_CHAT_ENTITY_MAX_SUGGESTIONS_PER_GROUP,
  TEAM_CHAT_ENTITY_TOKEN_REGEX,
  TEAM_CHAT_ENTITY_TRIGGER_REGEX,
} from './TeamChat.constants';

export const ENTITY_REFERENCE_TYPE = {
  BORROWER: 'borrower',
  INCOME: 'income',
  ASSET: 'asset',
  LIABILITY: 'liability',
  REO: 'reo',
};

export const ENTITY_REFERENCE_TYPE_ORDER = [
  ENTITY_REFERENCE_TYPE.BORROWER,
  ENTITY_REFERENCE_TYPE.INCOME,
  ENTITY_REFERENCE_TYPE.ASSET,
  ENTITY_REFERENCE_TYPE.LIABILITY,
  ENTITY_REFERENCE_TYPE.REO,
];

export const ENTITY_REFERENCE_TYPE_LABEL_SINGULAR = {
  [ENTITY_REFERENCE_TYPE.BORROWER]: 'Borrower',
  [ENTITY_REFERENCE_TYPE.INCOME]: 'Income',
  [ENTITY_REFERENCE_TYPE.ASSET]: 'Asset',
  [ENTITY_REFERENCE_TYPE.LIABILITY]: 'Liability',
  [ENTITY_REFERENCE_TYPE.REO]: 'REO',
};

export const ENTITY_REFERENCE_TYPE_LABEL = {
  [ENTITY_REFERENCE_TYPE.BORROWER]: 'Borrowers',
  [ENTITY_REFERENCE_TYPE.INCOME]: 'Income',
  [ENTITY_REFERENCE_TYPE.ASSET]: 'Assets',
  [ENTITY_REFERENCE_TYPE.LIABILITY]: 'Liabilities',
  [ENTITY_REFERENCE_TYPE.REO]: 'Real Estate Owned',
};

// SYSTEM_COLORS palette names (constants/colors.js) — resolved via getLabelColors so the chip
// stays theme/dark-mode aware without a hand-rolled hex per type.
export const ENTITY_REFERENCE_COLOR_NAME_BY_TYPE = {
  [ENTITY_REFERENCE_TYPE.BORROWER]: 'Azure',
  [ENTITY_REFERENCE_TYPE.INCOME]: 'Emerald',
  [ENTITY_REFERENCE_TYPE.ASSET]: 'Teal',
  [ENTITY_REFERENCE_TYPE.LIABILITY]: 'Coral',
  [ENTITY_REFERENCE_TYPE.REO]: 'Violet',
};

export const ENTITY_REFERENCE_ICON_BY_TYPE = {
  [ENTITY_REFERENCE_TYPE.BORROWER]: UserIcon,
  [ENTITY_REFERENCE_TYPE.INCOME]: DollarIcon,
  [ENTITY_REFERENCE_TYPE.ASSET]: AssetIcon,
  [ENTITY_REFERENCE_TYPE.LIABILITY]: DrawCreditCardIcon,
  [ENTITY_REFERENCE_TYPE.REO]: HomeIcon,
};

const ENTITY_REFERENCE_LABEL_FORBIDDEN_REGEX = /(]]|\r?\n)/g;

export const sanitizeEntityLabel = label =>
  (label ?? '').replace(ENTITY_REFERENCE_LABEL_FORBIDDEN_REGEX, '').trim();

export const buildEntityToken = ({ entityType, entityId, label }) =>
  `[[${entityType}:${entityId}|${sanitizeEntityLabel(label)}]]`;

export const getEntityReferenceKey = ({ entityType, entityId }) => `${entityType}:${entityId}`;

// Below this the exact-label soft-link would fire on trivially-short labels (e.g. a two-char
// nickname), where a coincidental typed word is far more likely than an intended reference.
const SOFT_LINKIFY_MIN_LABEL_LENGTH = 4;

/**
 * Conservative "did the user just type an entity's full name?" detector for the soft-linkify hint.
 * Returns a match `{ entityType, entityId, label, start, end }` ONLY when the text immediately
 * before the caret ends with the EXACT (case-insensitive) full label of exactly ONE candidate at a
 * word boundary. Any ambiguity (0 or ≥2 matches), a too-short label, or a caret inside an
 * unterminated `[[…]]` token yields null so the hint never fights a real reference.
 */
export const getSoftLinkifyMatchAtCaret = ({ text, caret, candidatesByType }) => {
  if (_.isNil(text) || _.isNil(caret)) {
    return null;
  }

  const before = text.slice(0, caret);
  const lastTokenOpen = before.lastIndexOf('[[');
  const lastTokenClose = before.lastIndexOf(']]');

  if (lastTokenOpen > lastTokenClose) {
    return null;
  }

  const lowerBefore = before.toLowerCase();

  const matches = ENTITY_REFERENCE_TYPE_ORDER.flatMap(
    type => candidatesByType?.[type] ?? [],
  ).filter(candidate => {
    const label = candidate.label ?? '';

    if (
      label.length < SOFT_LINKIFY_MIN_LABEL_LENGTH ||
      !lowerBefore.endsWith(label.toLowerCase())
    ) {
      return false;
    }

    const boundaryIndex = before.length - label.length;

    if (boundaryIndex === 0) {
      return true;
    }

    const charBeforeLabel = before[boundaryIndex - 1];

    // A label sitting immediately after `#` is already a composer entity alias (DEV-5657) — the
    // soft-linkify hint must never re-offer to linkify it (that would fight the alias the user just
    // inserted). Requires a whitespace word boundary AND rules out the alias prefix explicitly.
    return charBeforeLabel !== '#' && /\s/.test(charBeforeLabel);
  });

  if (matches.length !== 1) {
    return null;
  }

  const [match] = matches;

  return {
    entityType: match.entityType,
    entityId: match.entityId,
    label: match.label,
    start: before.length - match.label.length,
    end: caret,
  };
};

const VALID_ENTITY_TYPES = new Set(Object.values(ENTITY_REFERENCE_TYPE));

const ENTITY_ID_GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Splits message text into `text` / `entityRef` segments. A structurally well-formed token whose
 * type or id fails validation (unknown entity type, non-GUID id) is treated as plain text rather
 * than rejected outright — a stale/edited token should never crash rendering.
 */
export const parseEntityTokens = text => {
  if (!text) {
    return [];
  }

  const matches = Array.from(text.matchAll(TEAM_CHAT_ENTITY_TOKEN_REGEX));

  const { segments, lastIndex } = matches.reduce(
    (acc, match) => {
      const matchStart = match.index ?? 0;
      const [raw, entityType, entityId, rawLabel] = match;
      const nextSegments = [...acc.segments];

      if (matchStart > acc.lastIndex) {
        nextSegments.push({
          type: 'text',
          value: text.slice(acc.lastIndex, matchStart),
          key: `text-${acc.lastIndex}`,
          start: acc.lastIndex,
        });
      }

      const isValid = VALID_ENTITY_TYPES.has(entityType) && ENTITY_ID_GUID_REGEX.test(entityId);

      nextSegments.push(
        isValid
          ? {
              type: 'entityRef',
              entityType,
              entityId,
              label: sanitizeEntityLabel(rawLabel),
              raw,
              key: `entityRef-${matchStart}`,
              start: matchStart,
            }
          : { type: 'text', value: raw, key: `text-${matchStart}`, start: matchStart },
      );

      return { segments: nextSegments, lastIndex: matchStart + raw.length };
    },
    { segments: [], lastIndex: 0 },
  );

  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      value: text.slice(lastIndex),
      key: `text-${lastIndex}`,
      start: lastIndex,
    });
  }

  return segments;
};

// Plain-text form for one-line PREVIEW surfaces (channel list, reply preview, pinned strip) —
// tokens collapse to their human label; message bodies keep the raw token for chip rendering.
export const entityTokensToPlainText = text =>
  parseEntityTokens(text)
    .map(segment => (segment.type === 'entityRef' ? segment.label : segment.value))
    .join('');

export const getEntityReferenceTokens = text =>
  parseEntityTokens(text).filter(segment => segment.type === 'entityRef');

export const getEntityQueryAtCaret = (text, caretPosition) => {
  if (_.isNil(text) || _.isNil(caretPosition)) {
    return null;
  }

  const before = text.slice(0, caretPosition);
  const match = before.match(TEAM_CHAT_ENTITY_TRIGGER_REGEX);

  if (!match) {
    return null;
  }

  return {
    query: match[1] ?? '',
    start: match.index,
    end: caretPosition,
  };
};

export const replaceEntityRangeWithText = ({ text, entityRange, insertion }) => {
  if (!entityRange) {
    return { text, caret: text?.length ?? 0 };
  }

  const next = `${text.slice(0, entityRange.start)}${insertion}${text.slice(entityRange.end)}`;

  return { text: next, caret: entityRange.start + insertion.length };
};

const matchesEntityQuery = (candidate, normalizedQuery) => {
  if (!normalizedQuery) {
    return true;
  }

  const haystack = `${candidate.label} ${candidate.secondary ?? ''} ${
    candidate.ownerName ?? ''
  }`.toLowerCase();

  return haystack.includes(normalizedQuery);
};

/**
 * Fuzzy-filters + groups the aggregated candidates for the `#` popup. Group order puts the
 * caller-resolved `priorityType` (the financial subtab currently open, when resolvable) first,
 * falling back to the fixed borrower/income/asset/liability/reo order. Empty groups are dropped.
 */
export const groupEntityReferenceCandidates = ({
  candidatesByType,
  query = '',
  priorityType = null,
}) => {
  const normalizedQuery = (query ?? '').trim().toLowerCase();
  const orderedTypes =
    priorityType && ENTITY_REFERENCE_TYPE_ORDER.includes(priorityType)
      ? [priorityType, ...ENTITY_REFERENCE_TYPE_ORDER.filter(type => type !== priorityType)]
      : ENTITY_REFERENCE_TYPE_ORDER;

  return orderedTypes.reduce((groups, type) => {
    const items = (candidatesByType?.[type] ?? [])
      .filter(candidate => matchesEntityQuery(candidate, normalizedQuery))
      .slice(0, TEAM_CHAT_ENTITY_MAX_SUGGESTIONS_PER_GROUP);

    if (items.length) {
      groups.push({ type, label: ENTITY_REFERENCE_TYPE_LABEL[type], items });
    }

    return groups;
  }, []);
};
